import {gql, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import React, {PropsWithChildren, useContext, useState} from 'react';
import {Typography, Link} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {GraphQLFormattedError} from 'graphql';
import {GET_ACCOUNT_REVIEWS} from '../components/AccountOverview/accountOverviewQueries';
import {
  SubmitHandover,
  SubmitHandover_submitHandover,
  SubmitHandoverVariables,
} from '../schema-types/SubmitHandover';
import useAccountId from './useAccountId';
import {
  SubmitSoftHandover,
  SubmitSoftHandover_submitSoftHandover,
  SubmitSoftHandoverVariables,
} from '../schema-types/SubmitSoftHandover';
import {useReviewContext} from '../contexts/ReviewContext';
import {SubmissionStatusType} from '../components/Common/types';
import {SaltusAny} from '../utils/SaltusAny';
import {useFeaturePermission} from '../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../schema-types/globalTypes';
import {getReviewSubmissionRedirect} from '../utils/getReviewSubmissionURL';
import {REVIEW_SUBMISSION_FRAGMENT} from '../graphql/fragments';
import {REVIEW_VALIDATION} from '../pages/HandoverSummaryPage/useReviewValidation';

export const SUBMIT_HANDOVER = gql`
  mutation SubmitHandover($reviewId: UUID!, $sendToCuro: Boolean) {
    submitHandover(reviewId: $reviewId, sendToCuro: $sendToCuro) {
      ok
      errors
      review {
        ...ReviewSubmissionFragment
      }
    }
  }

  ${REVIEW_SUBMISSION_FRAGMENT}
`;

export const SUBMIT_SOFT_HANDOVER = gql`
  mutation SubmitSoftHandover($reviewId: UUID!, $sendToCuro: Boolean) {
    submitSoftHandover(reviewId: $reviewId, sendToCuro: $sendToCuro) {
      ok
      errors
      review {
        ...ReviewSubmissionFragment
      }
    }
  }
  ${REVIEW_SUBMISSION_FRAGMENT}
`;

const useSubmitHandoverContextProvider = () => {
  const {accountId} = useAccountId();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {reviewValidation: review, allValid} = useReviewContext();
  const canUseReportPack = useFeaturePermission([
    GlobalPermissionEnum.can_access_report_pack,
  ]);
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatusType>('awaiting');
  const navigate = useNavigate();
  const [onSubmit, {loading: onSubmitLoading}] = useMutation<
    SubmitHandover,
    SubmitHandoverVariables
  >(SUBMIT_HANDOVER, {
    awaitRefetchQueries: true,
    refetchQueries: [
      // {
      //   query: GET_ACCOUNT_BASIC,
      //   variables: {
      //     accountId,
      //     idType: 'id',
      //   },
      // },
      {
        query: REVIEW_VALIDATION,
        variables: {
          valueId: review?.id,
        },
      },
      {
        query: GET_ACCOUNT_REVIEWS,
        variables: {
          accountId,
          idType: 'id',
          curoSync: true,
        },
      },
    ],
  });

  const [onSoftSubmit, {loading: onSoftSubmitLoading}] = useMutation<
    SubmitSoftHandover,
    SubmitSoftHandoverVariables
  >(SUBMIT_SOFT_HANDOVER, {
    refetchQueries: [
      // {
      //   query: GET_ACCOUNT_BASIC,
      //   variables: {
      //     accountId,
      //     idType: 'id',
      //   },
      // },
      {
        query: REVIEW_VALIDATION,
        variables: {
          valueId: review?.id,
        },
      },
      {
        query: GET_ACCOUNT_REVIEWS,
        variables: {
          accountId,
          idType: 'id',
        },
      },
    ],
  });

  const fullSubmit = async () => {
    setSubmissionStatus('submitting');
    const loadingSnackbar = notifyLoading();
    return onSubmit({
      variables: {
        reviewId: review?.id,
      },
    })
      .then((res) => {
        closeSnackbar(loadingSnackbar);
        if (!res.data?.submitHandover || !res.data.submitHandover.review) {
          throw Error('');
        }
        const navigateTo = getReviewSubmissionRedirect({
          reviewDetails: res.data.submitHandover.review,
          canUseReportPack,
        });
        handleSubmissionResponse(res.data.submitHandover, navigateTo, res?.errors);
      })
      .catch((err) => {
        closeSnackbar(loadingSnackbar);
        catchSubmit(err);
      })
      .finally(() => setSubmissionStatus('awaiting'));
  };

  const softSubmit = () => {
    setSubmissionStatus('submitting');
    const loadingSnackbar = notifyLoading();
    return onSoftSubmit({
      variables: {
        reviewId: review?.id,
      },
    })
      .then((res) => {
        closeSnackbar(loadingSnackbar);
        if (!res.data?.submitSoftHandover || !res.data.submitSoftHandover.review) {
          throw Error('');
        }
        const navigateTo = getReviewSubmissionRedirect({
          reviewDetails: res.data.submitSoftHandover.review,
          canUseReportPack,
        });
        handleSubmissionResponse(res.data.submitSoftHandover, navigateTo, res?.errors);
      })
      .catch((err) => {
        closeSnackbar(loadingSnackbar);
        catchSubmit(err);
      })
      .finally(() => setSubmissionStatus('awaiting'));
  };

  const notifyLoading = () => {
    closeSnackbar();
    const snackbarId = enqueueSnackbar(
      <>
        <Typography>Submitting...</Typography>
        <Typography>This may take up to 3-4 minutes</Typography>
      </>,
      {variant: 'info', persist: true}
    );
    return snackbarId;
  };

  const handleSubmissionResponse = (
    submissionResponse:
      | SubmitSoftHandover_submitSoftHandover
      | SubmitHandover_submitHandover,
    navigateTo: string,
    errors: readonly GraphQLFormattedError<Record<string, any>>[] | undefined = []
  ) => {
    const displayErrors = [
      ...errors.map((x) => x.message),
      ...(submissionResponse.errors ?? []),
    ].join(', ');

    if (
      submissionResponse.review?.goals?.some((x) => x?.isFailedSubmission && !x.isDeleted)
    ) {
      throw Error('A goal failed');
    } else if (
      submissionResponse.review?.recommendations?.some(
        (x) => x?.isFailedSubmission && !x.isDeleted
      )
    ) {
      throw Error('A recommendation failed');
    } else if (
      submissionResponse.review?.tasks?.some((x) => x?.isFailedSubmission && !x.isDeleted)
    ) {
      throw Error('A task failed');
    } else if (
      submissionResponse.review?.isFailedSubmission &&
      !submissionResponse.review?.isDeleted
    ) {
      throw Error('A recommendation, task, or goal has failed');
    } else if (submissionResponse.ok) {
      enqueueSnackbar('Handover submitted', {
        variant: 'success',
      });
      navigate({
        pathname: navigateTo,
      });
    } else if (displayErrors.length) {
      enqueueSnackbar(
        <>
          <Typography>{`Submission has failed: (${displayErrors}). ${window.location.href}`}</Typography>
          <Link
            href="https://saltus.freshdesk.com/support/tickets/new"
            target="_blank"
            rel="noreferrer"
            color="#fff"
            underline="none"
          >
            Please click here to open a support ticket, copying and pasting the error
            message above.
          </Link>
        </>,
        {
          variant: 'error',
          autoHideDuration: null,
        }
      );
    } else {
      throw Error('Unknown error');
    }
  };

  const catchSubmit = (err: SaltusAny) => {
    enqueueSnackbar(
      <>
        <Typography>{`Submission has failed (${err.message}). ${window.location.href}`}</Typography>
        <Link
          href="https://saltus.freshdesk.com/support/tickets/new"
          target="_blank"
          rel="noreferrer"
          color="#fff"
          underline="none"
        >
          Please click here to open a support ticket, copying and pasting the error
          message above.
        </Link>
      </>,
      {
        variant: 'error',
        autoHideDuration: null,
      }
    );
  };

  return {
    fullSubmit,
    softSubmit,
    catchSubmit,
    setSubmissionStatus,
    submissionStatus,
    onSPPDASubmit: onSubmit,
  };
};

const SubmitHandoverContext = React.createContext<
  ReturnType<typeof useSubmitHandoverContextProvider>
>({
  catchSubmit: () => {},
  fullSubmit: async (_navigateTo?: string) => {},
  onSPPDASubmit: (async () => {}) as any,
  setSubmissionStatus: () => {},
  softSubmit: async () => {},
  submissionStatus: 'awaiting',
});

export const useSubmitHandover = () => {
  const context = useContext(SubmitHandoverContext);
  return context;
};

export const SubmitHandoverContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const context = useSubmitHandoverContextProvider();
  return (
    <SubmitHandoverContext.Provider value={context}>
      {children}
    </SubmitHandoverContext.Provider>
  );
};
